import { ChevronDown, ChevronLeftNav, ChevronRightNav, Plus } from 'assets/icons';
import Logo from 'assets/images/Jobbiz.svg';
import CountBadge from 'components/badge/CountBadge';
import Button from 'components/button';
import useSlideOver from 'components/SlideOver/useSlideOver';
import Tooltips from 'components/tooltips/Tooltips';
import { CREATE_MENU_PANEL } from './CreateMenuPanel';
import UseContext from 'hooks/useContext';
import useCrmContext from 'hooks/useCrmContext';
import useTasksChanged from 'hooks/useTasksChanged';
import Usefetchdata from 'hooks/usefetchdata';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import crmApi from 'server/crm/crmApi';
import { ORANGE_COLORS } from 'utils/colors';
import {
  ADMIN_LINKS,
  ADMIN_SECTIONS,
  canUseCreateMenu,
  isAdminConsole,
  isLinkActive,
  linkLabel,
  visibleSections,
} from './sidebarLinks';

// Every page mounts its own sidebar: the open groups are kept in between.
const OPEN_GROUPS_KEY = 'sidebar-groups';
const readOpenGroups = () => {
  try {
    return JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY)) ?? {};
  } catch {
    return {};
  }
};
const saveOpenGroups = (openGroups) => {
  try {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups));
  } catch {
    // Storage unavailable: the groups fold back on the next page.
  }
};

// Every page mounts its own sidebar, and the open tasks count started again
// from nothing on each: it vanished until the api answered. The last count
// is kept for its user, shown at once, then refreshed.
let keptOpenTasks = { userId: null, count: 0 };

// The prototype's sidebar: rows of 40px, 20px icons, the page lit in orange
// on its tint, in semibold.
const GRAY_700 = '#374151';
const ROW =
  'flex w-full h-10 items-center gap-3 rounded-lg px-3 transition-colors duration-150';
// The links of a group: no icon, their label under the group's.
const SUB_ROW =
  'flex w-full h-9 items-center gap-3 rounded-lg pl-11 pr-3 transition-colors duration-150';
// From 769px the folded sidebar is a rail of icons, the links of a group too.
const RAIL_ROW = 'tab:justify-center tab:px-0';
const RAIL_SUB_ROW = 'tab:h-10 tab:justify-center tab:px-0';
const IDLE_ROW = 'font-normal text-gray-700 hover:bg-gray-100';
const IDLE_SUB_ROW = 'font-normal text-gray-600 hover:bg-gray-100 hover:text-gray-700';
// Same weight as the idle rows: only the colour says which page is open.
const ACTIVE_ROW = 'bg-yellow-50 font-normal text-yellow-500';

const Label = ({ rail, children }) => (
  <span className={`ft-sm min-w-0 flex-1 truncate text-left ${rail ? 'tab:hidden' : ''}`}>
    {children}
  </span>
);

// One row of the sidebar: an icon and its label, the label alone in a
// tooltip on the rail. `href` opens another site in a new tab.
const NavRow = ({ link, label, active, sub, rail, count, soonLabel }) => {
  const Icon = link.icon;
  const className = sub
    ? `${SUB_ROW} ${rail ? RAIL_SUB_ROW : ''} ${active ? ACTIVE_ROW : IDLE_SUB_ROW}`
    : `${ROW} ${rail ? RAIL_ROW : ''} ${active ? ACTIVE_ROW : IDLE_ROW}`;
  // A link of a group shows its icon on the rail only.
  let iconClass = 'flex';
  if (sub) iconClass = rail ? 'hidden tab:flex' : 'hidden';
  const content = (
    <>
      <span className={`shrink-0 ${iconClass}`}>
        <Icon wh={20} color={active ? ORANGE_COLORS[600] : GRAY_700} />
      </span>
      <Label rail={rail}>{label}</Label>
      {count > 0 && (
        <span className={rail ? 'tab:hidden' : ''}>
          <CountBadge value={count} variant="soft" />
        </span>
      )}
    </>
  );
  if (link.disabled)
    return (
      <li>
        <Tooltips text={`${label} · ${soonLabel}`} placement="right">
          <span className={`${className} cursor-default opacity-50 hover:bg-transparent`}>
            {content}
          </span>
        </Tooltips>
      </li>
    );
  return (
    <li>
      <Tooltips text={label} placement="right" disabled={!rail}>
        {link.href ? (
          <a
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
          >
            {content}
          </a>
        ) : (
          <Link
            to={link.to}
            aria-current={active ? 'page' : undefined}
            className={className}
          >
            {content}
          </Link>
        )}
      </Tooltips>
    </li>
  );
};

// A group folds its links like Viviers did, with a chevron to show it. Folded
// while one of its pages is open, its label turns orange: open, the page is.
const NavGroup = ({ group, label, open, holdsPage, rail, animate, onToggle, children }) => {
  const Icon = group.icon;
  const here = holdsPage && !open;
  return (
    <li>
      <Tooltips text={label} placement="right" disabled={!rail}>
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className={`${ROW} ${rail ? RAIL_ROW : ''} font-normal hover:bg-gray-100 ${
            here ? 'text-yellow-500' : 'text-gray-700'
          }`}
        >
          <span className="flex shrink-0">
            <Icon wh={20} color={here ? ORANGE_COLORS[600] : GRAY_700} />
          </span>
          <Label rail={rail}>{label}</Label>
          <span
            className={`flex shrink-0 text-gray-400 ${rail ? 'tab:hidden' : ''}`}
          >
            <span
              className={`flex transition-transform ${open ? '' : '-rotate-90'}`}
            >
              <ChevronDown wh={16} color="currentColor" />
            </span>
          </span>
        </button>
      </Tooltips>
      {open && (
        <ul
          className={`mt-1 flex flex-col gap-1 ${
            animate ? 'motion-safe:animate-expandIn' : ''
          }`}
        >
          {children}
        </ul>
      )}
    </li>
  );
};

/**
 * The « Créer » button, the dashboard, then the Commercial, Recrutement and
 * Paie et facturation groups; in the admin console, its own menu instead (a recruiter gets the planning and the companies
 * at the top level instead of the Commercial group). From 769px it folds into
 * a rail of icons; below, it is the drawer the mobile bar opens.
 */
const SidebarDesktop = () => {
  const {
    sidebarExpanded,
    setSidebarExpanded,
    sidebarOpen,
    setSidebarOpen,
    language,
  } = UseContext();
  const { userData } = Usefetchdata();
  const { toggleSlideOver } = useSlideOver();
  const { call, ready } = useCrmContext();
  // The tasks left to do, beside « Mes tâches », kept up as tasks change.
  const userId = userData?._id;
  const [openTasks, setOpenTasks] = useState(() =>
    userId && keptOpenTasks.userId === userId ? keptOpenTasks.count : 0
  );
  const countTasks = useCallback(() => {
    if (ready && canUseCreateMenu(userData)) {
      call(crmApi.countOpenTasks).then(({ count }) => {
        keptOpenTasks = { userId, count };
        setOpenTasks(count);
      }, () => {});
    }
  }, [call, ready, userData, userId]);
  useEffect(countTasks, [countTasks]);
  useTasksChanged(countTasks);
  const { pathname } = useLocation();
  const rail = !sidebarExpanded;
  // The admin console swaps the menu for its own.
  const admin = isAdminConsole(pathname);
  const sections = admin ? ADMIN_SECTIONS : visibleSections(userData);
  const pageGroup = sections.find((section) =>
    section.items?.some((link) => isLinkActive(link, pathname))
  )?.key;
  const [openGroups, setOpenGroups] = useState(readOpenGroups);
  // Only a group opened by hand slides in, not one a new page restores.
  const [animatedGroup, setAnimatedGroup] = useState(null);

  useEffect(() => saveOpenGroups(openGroups), [openGroups]);

  // The group of the page opens by itself; it can be folded afterwards.
  useEffect(() => {
    if (pageGroup) {
      setOpenGroups((groups) =>
        groups[pageGroup] ? groups : { ...groups, [pageGroup]: true }
      );
    }
  }, [pageGroup]);

  // Moving to another page closes the mobile drawer.
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname, setSidebarOpen]);

  useEffect(() => {
    localStorage.setItem('sidebar-expanded', sidebarExpanded);
    document.body.classList.toggle('sidebar-expanded', sidebarExpanded);
  }, [sidebarExpanded]);

  const toggleGroup = (key) => {
    setAnimatedGroup(key);
    setOpenGroups((groups) => ({ ...groups, [key]: !groups[key] }));
  };

  // « Créer » asks what to create (a task, a company, a mission), as on the
  // prototype.
  const openCreateMenu = () => {
    setSidebarOpen(false);
    toggleSlideOver(CREATE_MENU_PANEL);
  };

  const createLabel = language.getValue('sidebar.create', 'Créer');
  const foldLabel = rail
    ? language.getValue('sidebar.expand', 'Agrandir')
    : language.getValue('sidebar.collapse', 'Réduire');
  const row = (link, sub) => (
    <NavRow
      key={link.key}
      link={link}
      label={linkLabel(language, link)}
      active={isLinkActive(link, pathname)}
      sub={sub}
      rail={rail}
      count={link.key === 'tasks' ? openTasks : 0}
      soonLabel={language.getValue('sidebar.soon', 'Bientôt disponible')}
    />
  );

  return (
    <div
      id="sidebar"
      className={`no-scrollbar absolute left-0 top-0 z-40 flex h-screen w-64 shrink-0 flex-col overflow-y-scroll border-r border-gray-100 bg-white p-4 transition-all duration-200 ease-in-out tab:static tab:z-auto tab:translate-x-0 tab:overflow-y-auto ${
        rail ? 'tab:w-[80px]' : 'tab:w-64'
      } ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
    >
      <div
        className={`mb-10 flex justify-between pr-3 mm:px-2 ${
          rail ? 'tab:justify-center tab:pr-0' : ''
        }`}
      >
        <button
          type="button"
          className="text-gray-600 hover:text-slate-400 tab:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label={language.getValue('sidebar.close_menu', 'Fermer le menu')}
          aria-controls="sidebar"
        >
          <ChevronLeftNav />
        </button>
        <Link
          to="/dashboard"
          className={`flex w-24 items-center justify-center ${
            rail ? 'tab:hidden' : ''
          }`}
        >
          <img src={Logo} alt="Jobbiz" className="w-full" />
        </Link>
        <div className="mt-auto hidden justify-end pt-3 tab:inline-flex">
          <div className="px-3 py-2">
            <Tooltips text={foldLabel} placement="right">
              <button
                type="button"
                onClick={() => setSidebarExpanded(!sidebarExpanded)}
                aria-label={foldLabel}
                aria-expanded={!rail}
                aria-controls="sidebar"
              >
                <ChevronRightNav prop={rail ? '' : 'rotate-180'} />
              </button>
            </Tooltips>
          </div>
        </div>
      </div>

      {/* The links follow the user's role and accesses: until the profile is
          read, grey rows hold their place, so that the menu does not show
          half of itself, then fill up. */}
      {!admin && !userData?._id && (
        <div aria-busy="true" className="flex flex-col">
          <div className="pb-5">
            <span className="block h-10 animate-pulse rounded-lg bg-gray-200" />
          </div>
          <div className="flex flex-col gap-1">
            {[0, 1, 2, 3, 4, 5].map((row) => (
              <div key={row} className={`${ROW} ${rail ? RAIL_ROW : ''}`}>
                <span className="size-5 shrink-0 animate-pulse rounded bg-gray-200" />
                <span className={`h-3.5 flex-1 animate-pulse rounded bg-gray-200 ${rail ? 'tab:hidden' : ''}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {!admin && canUseCreateMenu(userData) && (
        <div className="flex flex-col pb-5">
          <Button size="SM" color="PRIMARY" className="h-10" onClick={openCreateMenu}>
            <div
              className={`flex items-center gap-2 whitespace-nowrap ${
                rail ? 'justify-start tab:justify-center tab:gap-0' : 'justify-start'
              }`}
            >
              <Plus wh={18} color="#fff" className="shrink-0" />
              <span className={rail ? 'tab:hidden' : ''}>{createLabel}</span>
            </div>
          </Button>
        </div>
      )}

      <nav hidden={!admin && !userData?._id}>
        <ul className="flex flex-col gap-1">
          {sections.map((section) =>
            section.items ? (
              <NavGroup
                key={section.key}
                group={section}
                label={linkLabel(language, section)}
                open={!!openGroups[section.key]}
                holdsPage={section.key === pageGroup}
                rail={rail}
                animate={animatedGroup === section.key}
                onToggle={() => toggleGroup(section.key)}
              >
                {section.items.map((link) => row(link, true))}
              </NavGroup>
            ) : (
              row(section, false)
            )
          )}
        </ul>
      </nav>
      {admin && <ul className="mt-auto flex flex-col pt-5">{row(ADMIN_LINKS.back, false)}</ul>}
    </div>
  );
};

export default SidebarDesktop;
